import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PurchaseDocDetail } from "types/InvoiceInbox";
import { verifyBankAccount } from "services/invoice-inbox.service";
import { formatAmount } from "utils/invoiceInboxStatus";
import { CompactBanner, RunChange, inputClass, primaryButtonClass } from "./invoice-doc-ui";

type Props = {
  companyId: string;
  detail: PurchaseDocDetail;
  busy: boolean;
  run: RunChange;
  /** The last change was refused (or the document could not be loaded). */
  error: string | null;
  /** The reload after a change failed: what is on screen is from before it. */
  refreshFailed: boolean;
  /** Why the last "send for approval" was refused, as blocker codes. */
  blockers: string[];
  blockNotice: string | null;
  onDismissBlockNotice: () => void;
  retryingExtraction: boolean;
  onRetryExtraction: () => void;
};

const normalizeIban = (value: string | null | undefined) => (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/** Everything that should stop the reader before they act, in order of how
 * much it should stop them — one line each, with its own action. */
export default function InvoiceDocBanners(props: Props) {
  const { t } = useTranslation();
  const { detail, error, refreshFailed, blockers, blockNotice } = props;
  const { doc, duplicateOf } = detail;

  return (
    <div className="space-y-2 px-4 pt-3 empty:hidden">
      {refreshFailed && (
        <CompactBanner
          tone="warning"
          message={t("invoice_inbox_refresh_failed", { defaultValue: "Could not refresh the document. Reload the page." })}
        />
      )}
      {error && <CompactBanner tone="critical" message={error} />}
      {doc.suspiciousContent && (
        <CompactBanner
          tone="critical"
          message={t("invoice_inbox_banner_suspicious", {
            defaultValue: "This document contains text addressed to the reader. Treat its contents with suspicion.",
          })}
        />
      )}
      {doc.bankChangeFlagged && <BankChangeBanner {...props} />}
      {doc.buyerEntityMatch === "mismatch" && (
        <CompactBanner
          tone="warning"
          message={t("invoice_inbox_banner_entity", {
            defaultValue: "This invoice is addressed to a different company in the group.",
          })}
        />
      )}
      {duplicateOf && (
        <CompactBanner
          tone="warning"
          message={
            <>
              {t("invoice_inbox_banner_duplicate", { defaultValue: "Possibly the same as" })} #{duplicateOf.docNumber} —{" "}
              {duplicateOf.invoiceNumber} ({formatAmount(duplicateOf.grossAmount)})
            </>
          }
        />
      )}
      {doc.extractionStatus === "failed" && (
        <CompactBanner
          tone="warning"
          message={t("invoice_inbox_banner_unread", {
            defaultValue:
              "This document could not be read automatically. The file is here — the fields can be filled in by hand.",
          })}
          // A failure is often about something other than the document — a
          // model call that never got through, a timeout. Without this the
          // only way to ask again was to upload the same file a second time,
          // which then reads as a duplicate.
          action={
            <button
              type="button"
              className="whitespace-nowrap underline underline-offset-2 disabled:opacity-50"
              disabled={props.retryingExtraction}
              onClick={props.onRetryExtraction}
            >
              {t("invoice_inbox_reextract", { defaultValue: "Read it again" })}
            </button>
          }
        />
      )}
      {blockers.length > 0 && (
        <CompactBanner
          tone="warning"
          message={
            <>
              {t("invoice_inbox_cannot_dispatch", { defaultValue: "Before this can go for approval:" })}{" "}
              {blockers.map((b) => t(`invoice_inbox_blocker_${b}`, { defaultValue: b })).join(" · ")}
            </>
          }
        />
      )}
      {blockNotice && <CompactBanner tone="info" message={blockNotice} onDismiss={props.onDismissBlockNotice} />}
    </div>
  );
}

/** The account on the invoice is not one we have confirmed for this supplier:
 * the warning, the accounts used before, and — for an account still waiting —
 * the confirmation with its mandatory "who did you call" note. */
function BankChangeBanner({ companyId, detail, busy, run }: Props) {
  const { t } = useTranslation();
  const [verifyNote, setVerifyNote] = useState("");
  const { doc, bankAccounts } = detail;
  // The account on THIS document, not whichever pending row came back first.
  // A supplier can have several waiting — a spoofed invoice creates one and the
  // supplier's genuine new account creates another — and confirming the wrong
  // one trusts an attacker's IBAN and releases their held documents.
  const pendingBank = bankAccounts.find(
    (a) => a.status === "pending" && normalizeIban(a.ibanNormalized) === normalizeIban(doc.supplierIbanRaw)
  );
  const noteLabel = t("invoice_inbox_verify_note", { defaultValue: "Who did you speak to, and on which number?" });

  return (
    <CompactBanner
      tone="critical"
      message={
        <>
          {t("invoice_inbox_banner_bank", {
            defaultValue:
              "The bank account on this invoice is not one we have confirmed for this supplier. Call them on a number from your own records — not one printed here — before it goes any further.",
          })}{" "}
          <span className="font-mono text-xs">{doc.supplierIbanRaw || "—"}</span>
        </>
      }
    >
      <div className="space-y-2">
        {bankAccounts
          .filter((a) => a.status === "trusted")
          .map((a) => (
            <div key={a.id} className="text-xs text-gray-600">
              {t("invoice_inbox_previous_account", { defaultValue: "Previously used" })}:{" "}
              <span className="font-mono">{a.ibanDisplay || a.ibanNormalized}</span>
            </div>
          ))}
        {pendingBank && (
          <>
            <textarea
              rows={2}
              aria-label={noteLabel}
              placeholder={noteLabel}
              className={inputClass}
              value={verifyNote}
              onChange={(event) => setVerifyNote(event.target.value)}
            />
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className={primaryButtonClass}
                disabled={busy || !verifyNote.trim()}
                onClick={() =>
                  run(() =>
                    verifyBankAccount(companyId, pendingBank.id, {
                      note: verifyNote,
                      // Sent so the server can refuse if this is not the
                      // account printed on the document being looked at.
                      docId: doc.id,
                    })
                  )
                }
              >
                {t("invoice_inbox_confirm_account", { defaultValue: "Confirm this account" })}
              </button>
              <span className="text-xs">
                {t("invoice_inbox_confirming_account", { defaultValue: "Confirming" })}:{" "}
                <span className="font-mono">{pendingBank.ibanDisplay || pendingBank.ibanNormalized}</span>
              </span>
            </div>
          </>
        )}
      </div>
    </CompactBanner>
  );
}
